import {
    HumanizeDurationOptions,
    Language,
    Piece,
    UnitName
} from "./human.interface";
import { HumanizeDurationLanguage } from "./human.lang";

const languageService = new HumanizeDurationLanguage();

const units = ["y", "mo", "w", "d", "h", "m", "s"] as const;
const unitMeasures = {
    y: 31557600000,
    mo: 2629800000,
    w: 604800000,
    d: 86400000,
    h: 3600000,
    m: 60000,
    s: 1000,
    ms: 1
}

const defaultOptions: HumanizeDurationOptions = {
    language: "en",
    delimiter: " ",
    spacer: " ",
    conjunction: "",
    serialComma: true,
    languages: {},
    largest: 10,
    decimal: ".",
    round: false,
    greatest: false,
};

export class HumanizeDuration {
  
    humanize(value: number, paramOptions: Record<string, any>) {
        const options = {
            ...defaultOptions,
            ...paramOptions,
        }

        return this.doHumanization(value, options);
    }
  
    private doHumanization(ms: number, options: HumanizeDurationOptions) {
        let i: number, len: number, piece: Piece;
    
        // Make sure we have a positive number.
        // Has the nice sideffect of turning Number objects into primitives.
        ms = Math.abs(ms);

        const { language = 'ru' } = options;
    
        const dictionary = languageService.languages[language];
        if (!dictionary) {
            throw new Error("No language " + dictionary + ".");
        }
    
        const pieces: Piece[] = [];
    
        // Start at the top and keep removing units, bit by bit.
        let unitName: UnitName;
        let unitMS: number, unitCount: number;
        for (i = 0, len = units.length; i < len; i++) {
            unitName = units[i];
            unitMS = unitMeasures[unitName];
    
            // What's the number of full units we can fit?
            if (i + 1 === len) {
                unitCount = ms / unitMS;
            } else {
                unitCount = Math.floor(ms / unitMS);
            }
    
            // Add the string.
            pieces.push({
                unitCount: unitCount,
                unitName: unitName
            });
    
            // Remove what we just figured out.
            ms -= unitCount * unitMS;
        }
    
        let firstOccupiedUnitIndex = 0;
        for (i = 0; i < pieces.length; i++) {
            if (pieces[i].unitCount) {
                firstOccupiedUnitIndex = i;
                break;
            }
        }
    
        if (options.round) {
            let ratioToLargerUnit: number, previousPiece: Piece;

            for (i = pieces.length - 1; i >= 0; i--) {
                piece = pieces[i];
                piece.unitCount = Math.round(piece.unitCount);
        
                if (i === 0) {
                    break;
                }
        
                previousPiece = pieces[i - 1];
        
                ratioToLargerUnit =
                    unitMeasures[previousPiece.unitName] /
                    unitMeasures[piece.unitName];

                if (
                    piece.unitCount % ratioToLargerUnit === 0 ||
                    (options.largest && options.largest - 1 < i - firstOccupiedUnitIndex)
                ) {
                    previousPiece.unitCount += piece.unitCount / ratioToLargerUnit;
                    piece.unitCount = 0;
                }
            }
        }
    
        const result: string[] = [];
        for (i = 0, pieces.length; i < len; i++) {
            piece = pieces[i];
            
            if (piece.unitCount) {
                result.push(
                    this.render(piece.unitCount, piece.unitName, dictionary, options)
                );
            }
    
            if (result.length === options.largest) {
                break;
            }
        }
    
        if (result.length) {
            if (options.greatest) {
                return result[0];
            }

            if (!options.conjunction || result.length === 1) {
                return result.join(options.delimiter);
            }
            
            if (result.length === 2) {
                return result.join(options.conjunction);
            }
            
            if (result.length > 2) {
                return (
                    result.slice(0, -1).join(options.delimiter) +
                    (options.serialComma ? "," : "") +
                    options.conjunction +
                    result.slice(-1)
                );
            }
        }
        
        return this.render(
            0,
            units[units.length - 1],
            dictionary,
            options
        );
    }
  
    private render(
        count: number,
        type: UnitName,
        dictionary: Language,
        options: HumanizeDurationOptions
    ) {
        let decimal: string;
        if (options.decimal === void 0) {
            decimal = dictionary.decimal;
        } else {
            decimal = options.decimal;
        }
  
        const countStr = count.toString().replace(".", decimal.toString());
    
        const word: string = dictionary[type](count);
    
        return countStr + options.spacer + word;
    }
}
